import {
  Button,
  Col,
  Form,
  Popover,
  Row,
  useCSV,
  Widget,
  WizardItemType,
  Zone,
} from '@gimlite/ui-v1';
import { useLazyQuery, useTranslation } from '@gimlite/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ContractEntity,
  Query,
  QueryContractsArgs,
  QueryGlobalSearchArgs,
} from '../../../../client/graphql';
import {
  DefaultContractDetailsWizard,
  DefaultCredentialsQrcodeWizard,
} from '../../../../common/defaultValue/wizard';
import { globalSearchGQL } from '../../../../common/gql/globalSearch.gql';
import { PopupInfo } from '../../../../components/popup-info/popup-info.component';
import { SwitcherText } from '../../../../components/switcher-text/switcher-text.component';

import { contractsGql } from '../../../../common/gql/contracts.gql';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import { ContractDetailsForm } from '../../../../form/contract-create/contract-create.form';
import {
  ContractCSVParams,
  ContractsCreateFormType,
} from '../../../../form/contracts-create/contracts-create.form';
import {
  SingleCheckForm,
  SingleCheckFormType,
} from '../../../../form/single-check/single-check.form';
import { AddSeveralMotoristsToAnExistingPoolContractWizardContext } from '../../../addSeveralMotoristsToAnExistingPoolContract/addSeveralMotoristsToAnExistingPoolContract.wizard';
import { AddOneMotoristToAnExistingPoolContractWizardContext } from '../../addOneMotoristToAnExistingPoolContract.wizard';

const pageLimit = 50;

export const ParkerStep = ({
  context: {
    contractBase: contract,
    credentials,
    contractDetails,
    contractExistedSave,
    contractMode,
    selectExistContract,
    parkingId,
    product: { id: productId, credentials: productCredentials },
  },
  validate,
  submit,
}: WizardItemType.Config.Component<AddOneMotoristToAnExistingPoolContractWizardContext>) => {
  const { t, lang } = useTranslation();

  const [searchLocalMotoristResult, setSearchLocalMotoristResult] = useState<
    ContractEntity[] | null
  >(null);

  const [contractsCall, contractsQuery] = useLazyQuery<
    { contracts: Query['contracts'] },
    QueryContractsArgs
  >(contractsGql, {
    variables: {
      page: 1,
      limit: pageLimit,
      parkingId: parkingId,
      order: 'name1',
      categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
      isNotInPeriod: [contract.startDate!, contract.endDate!],
    },
  });

  const [globalSearchCall, globalSearchQuery] = useLazyQuery<
    { globalSearch: Query['globalSearch'] },
    QueryGlobalSearchArgs
  >(globalSearchGQL);

  useEffect(() => {
    (async () => {
      try {
        await contractsCall({
          variables: {
            page: 1,
            limit: pageLimit,
            order: 'name1',
            parkingId: parkingId,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contract.startDate!, contract.endDate!],
          },
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [parkingId, contract.startDate, contract.endDate]);

  const globalSearch = useMemo(() => {
    if (!globalSearchQuery.data) return null;

    return globalSearchQuery.data?.globalSearch;
  }, [globalSearchQuery.data?.globalSearch]);

  const contracts = useMemo(() => {
    if (!contractsQuery.data) return null;

    return contractsQuery.data?.contracts;
  }, [contractsQuery.data?.contracts]);

  const motoristFormattedLine = useCallback((motorists: ContractEntity[]) => {
    return motorists.map(
      ({
        _id,
        name1,
        name2,
        username,
        startDate,
        stopDate,
        isEnabled,
        ospContractId,
      }) => ({
        _id,
        items: {
          component: (
            <ContractMotoristLine
              config={{
                action: false,
              }}
              data={{
                contractId: ospContractId!,
                firstName: name1 || '',
                lastName: name2 || '',
                status: statusHelper({
                  stopDate: new Date(stopDate!),
                  startDate: new Date(startDate),
                  isEnabled: isEnabled,
                }),
              }}
            />
          ),
        },
      }),
    );
  }, []);

  const { fresh, paging: pagingFormatted } = useMemo((): {
    fresh: ContractEntity[] | undefined;
    paging?: SingleCheckFormType.Props['data']['paging'];
  } => {
    if (!contracts?.list) {
      return { fresh: undefined };
    } else {
      const listTarget = !searchLocalMotoristResult
        ? contracts.list
        : searchLocalMotoristResult;

      return {
        fresh: listTarget,
        paging: !searchLocalMotoristResult
          ? contracts.paging
          : {
              count: searchLocalMotoristResult.length,
              current: 1,
              limit: searchLocalMotoristResult.length,
            },
      };
    }
  }, [contracts?.list, contracts?.paging, searchLocalMotoristResult]);

  const handleSearch = useCallback(
    async (value: string) => {
      if (value && value.length >= 3) {
        const { data } = await globalSearchCall({
          variables: {
            content: value,
            parkingIds: parkingId ? [parkingId] : undefined,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contract.startDate!, contract.endDate!],
          },
        });

        const contracts = data?.globalSearch.contracts;

        setSearchLocalMotoristResult(() => contracts || []);
        submit({
          contractExistedSave: [
            ...contractExistedSave,
            ...(data?.globalSearch?.contracts || []),
          ].reduce((acc: ContractEntity[], { _id, ...rest }) => {
            if (!acc.find((element) => element._id === _id)) {
              return [...acc, { _id, ...rest }];
            }
            return acc;
          }, []),
        });

        try {
          await contractsCall({
            variables: {
              page: 1,
              limit: pageLimit,
              order: 'name1',
              parkingId: parkingId,
              categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
              isNotInPeriod: [contract.startDate!, contract.endDate!],
            },
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        setSearchLocalMotoristResult(() => null);
      }
    },
    [parkingId, contract.startDate, contract.endDate],
  );

  useEffect(() => {
    if (contractMode === 'exist') {
      const newList = contractExistedSave;

      if (contracts?.list) {
        contracts.list.forEach(({ _id, ...rest }) => {
          if (
            contractExistedSave.findIndex((element) => element._id === _id) ===
            -1
          ) {
            newList.push({ _id, ...rest });
          }
        });
      }

      submit({
        contractExistedSave: newList,
      });
    }
  }, [contracts?.list, contractExistedSave, contractMode]);

  useEffect(() => {
    if (contractMode === 'exist') {
      (async () => {
        try {
          await contractsCall({
            variables: {
              page: 1,
              limit: pageLimit,
              order: 'name1',
              parkingId: parkingId,
              categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
              isNotInPeriod: [contract.startDate!, contract.endDate!],
            },
          });
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [contractMode, parkingId, contract.startDate, contract.endDate]);

  useEffect(() => {
    if (
      !!!credentials.actived.find(({ name }) => name === 'QRCODE') &&
      productCredentials.find((item) => item === 'QRCODE') &&
      lang
    ) {
      submit({
        addFakeQRCODE: true,
        credentials: {
          ...credentials,
          actived: [
            DefaultCredentialsQrcodeWizard({
              t,
              lang,
            }),
            ...credentials.actived,
          ],
        },
      });
    }
  }, [credentials.actived, lang]);

  useEffect(() => {
    console.log(
      '🚀 ~ file: parker.step.tsx:292 ~ selectExistContract:',
      selectExistContract,
      [...(fresh ? fresh : []), ...contractExistedSave],
    );
    if (selectExistContract) {
      submit({
        contractDetails: {
          ...contractDetails,
          exist: [...(fresh ? fresh : []), ...contractExistedSave].find(
            (element) => element._id === selectExistContract,
          )!,
        },
      });
    }
  }, [fresh, contractExistedSave, selectExistContract]);

  useEffect(() => {
    if (contractMode === 'exist') validate(!!contractDetails.exist);
  }, [contractDetails.exist, contractMode]);

  const credentialsEdit = {
    RFID: productCredentials.includes('RFID'),
    PLATE: productCredentials.includes('PLATE'),
    TCS: productCredentials.includes('TCS'),
  };
  const CSVParams = ContractCSVParams(credentialsEdit);
  const { name1, name2, username } = contractDetails.create;
  const { CombinationValidNode } = useCSV<ContractsCreateFormType.Extra.CSV>({
    valid: CSVParams.valid,
    fields: CSVParams.fields.map(({ label, ...rest }) => ({
      ...rest,
      label: t(label),
    })),
    data: [
      {
        uid: !!credentials.actived.find(({ name }) => name === 'TCS')
          ? '123456789'
          : null,
        plate: credentials.actived.find(({ name }) => name === 'PLATE')
          ? '123456789'
          : null,
        visibleId: !!credentials.actived.find(({ name }) => name === 'TCS')
          ? '123456789'
          : null,
        firstName: name1,
        lastName: name2,
        email: username,
      },
    ],
  });

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'form']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            APBMode: contract.APBMode,
            APBNext: contract.APBMode !== 'NONE' ? contract.APBNext : null,
            state: contract.state,
            startDate: contract.startDate,
            endDate: contract.endDate,
            description: contract.description,
            reference: contract.reference,
            articleCode: contract.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'form' }}>
        <Zone
          config={{
            gap: {
              y: 0,
              x: 0,
            },
            zones: [
              // ['link', 'empty'],
              ['work', 'work'],
            ],
            rows: ['1fr'],
            columns: ['1fr', 'min-content'],
          }}
        >
          {/* <Zone.Area config={{ area: 'link' }}>
            <Widget config={{ shadow: false, full: true }}>
              <SwitcherText<
                AddSeveralMotoristsToAnExistingPoolContractWizardContext['contractMode']
              >
                handleEvent={{
                  mode: (value) => {
                    submit({
                      contractMode: value,
                      contractDetails: {
                        create: DefaultContractDetailsWizard({ t, lang }),
                        exist: null,
                      },
                      selectExistContract: null,
                      credentials: {
                        actived: productCredentials.includes('QRCODE')
                          ? [DefaultCredentialsQrcodeWizard({ t, lang })]
                          : [],
                        desactived: [],
                        context: 'wizardNewContract',
                      },
                    });

                    validate(false);
                  },
                }}
                data={{ value: contractMode }}
                config={{
                  mode: [
                    {
                      label: t('createContract?'),
                      value: 'exist',
                    },
                    {
                      label: t('chooseExistingContract?'),
                      value: 'create',
                    },
                  ],
                }}
              />
            </Widget>
          </Zone.Area> */}

          <Zone.Area config={{ area: 'empty' }}>
            <div></div>
          </Zone.Area>
          <Zone.Area config={{ area: 'csv' }}>
            <Col config={{ vertical: 'start' }}>
              <Row>
                {contractMode === 'create' ? (
                  <Popover
                    data={
                      <PopupInfo
                        data={{
                          node: CombinationValidNode,
                        }}
                        config={{
                          title: t('contractFieldCombinations'),
                          description: [
                            {
                              key: 'required',
                              label: t('required'),
                              value: '*',
                              color: 'error',
                            },
                          ],
                        }}
                      />
                    }
                  >
                    <Button
                      config={{
                        size: 'small',
                        mode: 'stroke',
                        text: t('validContractFieldsCombinations'),
                      }}
                    ></Button>
                  </Popover>
                ) : (
                  <></>
                )}
              </Row>
            </Col>
          </Zone.Area>
          <Zone.Area config={{ area: 'work' }}>
            <>
              {contractMode === 'create' ? (
                <Form
                  data={{ value: contractDetails.create }}
                  config={{ validateOnChange: true }}
                  handleEvent={{
                    change: (value) => {
                      submit({
                        contractDetails: { ...contractDetails, create: value },
                      });
                    },
                    validate: (value: boolean) => {
                      validate(value);
                    },
                  }}
                >
                  <ContractDetailsForm
                    handleEvent={{
                      submitCredentials: (value) =>
                        submit({ credentials: value }),
                      submitContract: (value) => {
                        submit({
                          contractDetails: {
                            ...contractDetails,
                            create: value,
                          },
                        });
                      },
                      validate: () => {
                        const activedCredentials: {
                          uid?: Boolean;
                          visibleId?: Boolean;
                          plate?: Boolean;
                        } = credentials.actived.reduce((acc, credential) => {
                          switch (credential.name) {
                            case 'PLATE':
                              return { ...acc, plate: true };
                            case 'RFID':
                              return {
                                ...acc,
                                uid: true,
                                visibleId: credential.visibleId ? true : false,
                              };
                            case 'QRCODE':
                            default:
                              return acc;
                          }
                        }, {});

                        const currentCombinaison = Object.entries({
                          ...contractDetails.create,
                          ...activedCredentials,
                        }).reduce((acc: any, [key, value]) => {
                          return value ? [...acc, key] : acc;
                        }, []);

                        const credentialsEdit = {
                          RFID: productCredentials.includes('RFID'),
                          PLATE: productCredentials.includes('PLATE'),
                          TCS: productCredentials.includes('TCS'),
                        };

                        const valid = ContractCSVParams(
                          credentialsEdit,
                        ).valid.map((combinaison) =>
                          combinaison.map((item) =>
                            item === 'email'
                              ? 'username'
                              : item === 'firstName'
                                ? 'name1'
                                : item === 'lastName'
                                  ? 'name2'
                                  : item,
                          ),
                        );

                        const findValidCombinaison = valid.findIndex(
                          (combinaison) =>
                            combinaison.length === 0 ||
                            combinaison.every((item) =>
                              currentCombinaison.includes(item),
                            ),
                        );

                        validate(
                          findValidCombinaison !== -1
                            ? true
                            : t('watermelon-INVALID_COMBINAISON'),
                        );
                      },
                    }}
                    data={{
                      credentials,
                      contract: contractDetails.create,
                    }}
                    config={{
                      credentialsEdit: {
                        RFID: productCredentials.includes('RFID'),
                        PLATE: productCredentials.includes('PLATE'),
                        TCS: productCredentials.includes('TCS'),
                      },
                    }}
                  />
                </Form>
              ) : (
                <Widget
                  config={{
                    title: t('listOfExistingContracts'),
                    shadow: false,
                  }}
                  state={{
                    loading: contractsQuery.loading,
                    refetch: contractsQuery.refetch,
                    error: contractsQuery.error,
                    showPermanentRefetch: false,
                  }}
                >
                  <SingleCheckForm
                    handleEvent={{
                      paging: async (data) => {
                        try {
                          await contractsCall({
                            variables: {
                              page: data.page,
                              limit: data.limit,
                              order: 'name1',
                              parkingId: parkingId,
                              categories: [
                                'PERIOD_SUBSCRIPTION',
                                'MOTORIST_POOL',
                              ],
                              isNotInPeriod: [
                                contract.startDate!,
                                contract.endDate!,
                              ],
                            },
                          });
                        } catch (error) {
                          console.error(error);
                        }
                      },
                      checkItem: (id) => {
                        submit({
                          selectExistContract: id,
                        });
                      },
                      search: handleSearch,
                    }}
                    data={{
                      check: contractDetails.exist
                        ? contractDetails.exist._id
                        : null,
                      fresh: fresh ? motoristFormattedLine(fresh) : undefined,
                      saved: motoristFormattedLine(contractExistedSave),
                      paging: pagingFormatted,
                    }}
                    config={{
                      searchPlaceholder: t('searchAMotorist'),
                    }}
                  />
                </Widget>
              )}
            </>
          </Zone.Area>
        </Zone>
      </Zone.Area>
    </Zone>
  );
};
